import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { useAppStore } from './stores/useAppStore';
import { ErrorBoundary } from './components/ErrorBoundary';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import { BookManagementPage } from './pages/BookManagementPage';
import { ResourceManagementPage } from './pages/ResourceManagementPage';
import LessonPlanManagementPage from './pages/LessonPlanManagementPage';
import { SearchPage } from './pages/SearchPage';
import ReportsPage from './pages/ReportsPage';
import UserProfilePage from './pages/UserProfilePage';
import UserSettingsPage from './pages/UserSettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import { BackupManagementPage } from './pages/BackupManagementPage';
import { OverdueBooksPage } from './pages/OverdueBooksPage';
import { BookLoansPage } from './pages/BookLoansPage';
import { CartPage } from './pages/CartPage';
import { CategoryManagementPage } from './pages/CategoryManagementPage';
import { ResourceTopicManagementPage } from './pages/ResourceTopicManagementPage';
import { SubjectManagementPage } from './pages/SubjectManagementPage';
import AdminTenantsPage from './pages/AdminTenantsPage';
import AIKnowledgeManagementPage from './pages/AIKnowledgeManagementPage';
import { LessonPlanTemplateManagement } from './pages/tabs/LessonPlanTemplateManagement';
import PublicResourcePage from './pages/PublicResourcePage';
import OfficialDocumentsPage from './pages/OfficialDocumentsPage';
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        '*::before, *::after': {
          boxSizing: 'border-box',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAppStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Role-based Protected Route Component
const RoleProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles: string[]
}> = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useAppStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Super-admin can access any route (bypass role check)
  if (user?.isSuperAdmin === true) {
    return <>{children}</>;
  }

  if (!user || !user.roles.some(role => allowedRoles.includes(role))) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

import { AIChatWidget } from './components/AIChatWidget';

// Layout Component for Protected Routes
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAppStore();

  useEffect(() => {
    // Update browser tab title + description based on school name (tenant context)
    const schoolName =
      (user as any)?.schoolName ||
      (() => {
        try {
          return localStorage.getItem('schoolName');
        } catch {
          return null;
        }
      })();

    const baseTitle = schoolName ? `Thư viện số ${schoolName}` : 'Thư viện số';
    const suffix = user?.isSuperAdmin ? ' - Quản trị' : '';
    document.title = `${baseTitle}${suffix}`;

    const desc = schoolName
      ? `Hệ thống ${baseTitle}`
      : 'Hệ thống thư viện số';

    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = desc;
  }, [location.pathname, location.search, isAuthenticated, user?.isSuperAdmin, (user as any)?.schoolName]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      position: 'relative'
    }}>
      <Box sx={{
        flexShrink: 0,
        width: '100%',
        position: 'relative',
        zIndex: 1100
      }}>
        <Header />
      </Box>
      <Box component="main" sx={{
        flexGrow: 1,
        bgcolor: 'background.default',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        minWidth: 0
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: '100%',
          flexGrow: 1,
          py: 3,
          px: { xs: 2, sm: 3, md: 4 },
          boxSizing: 'border-box'
        }}>
          {children}
        </Box>
      </Box>

      {/* AI Assistant Widget */}
      {isAuthenticated && <AIChatWidget />}
    </Box>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/books"
              element={
                <ProtectedRoute>
                  <Layout>
                    <BookManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/books/loans"
              element={
                <RoleProtectedRoute allowedRoles={['Librarian', 'Approver', 'Admin']}>
                  <Layout>
                    <BookLoansPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/resources"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ResourceManagementPage excludeCategoryName="Công Văn" />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lesson-plans"
              element={
                <ProtectedRoute>
                  <Layout>
                    <LessonPlanManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lesson-plans/templates"
              element={
                <RoleProtectedRoute allowedRoles={['Admin', 'Approver', 'Librarian']}>
                  <Layout>
                    <LessonPlanTemplateManagement />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SearchPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/official-documents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <OfficialDocumentsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <RoleProtectedRoute allowedRoles={['Admin', 'Approver', 'Librarian']}>
                  <Layout>
                    <ReportsPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserProfilePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <UserSettingsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/ai-knowledge"
              element={
                <RoleProtectedRoute allowedRoles={['Admin']}>
                  <Layout>
                    <AIKnowledgeManagementPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/backups"
              element={
                <RoleProtectedRoute allowedRoles={['Admin']}>
                  <Layout>
                    <BackupManagementPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <RoleProtectedRoute allowedRoles={['Admin']}>
                  <Layout>
                    <UserManagementPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/cart"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CartPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <RoleProtectedRoute allowedRoles={['Librarian', 'Approver', 'Admin']}>
                  <Layout>
                    <CategoryManagementPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/resource-topics"
              element={
                <RoleProtectedRoute allowedRoles={['Librarian', 'Approver', 'Admin', 'Teacher']}>
                  <Layout>
                    <ResourceTopicManagementPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/subjects"
              element={
                <RoleProtectedRoute allowedRoles={['Approver', 'Admin']}>
                  <Layout>
                    <SubjectManagementPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/tenants"
              element={
                <RoleProtectedRoute allowedRoles={['Admin']}>
                  <Layout>
                    <AdminTenantsPage />
                  </Layout>
                </RoleProtectedRoute>
              }
            />
            {/* Public routes - no authentication required */}
            <Route
              path="/public/resource/:id"
              element={<PublicResourcePage />}
            />
            <Route
              path="/public/lesson-plan/:id"
              element={<PublicResourcePage />}
            />
          </Routes>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;